/**
 * usePanelStateSync
 * 
 * Custom hook to synchronize panel minimization state with immersive mode
 * Handles panel state changes based on activeTab and panelState props
 * 
 * @param {boolean} isImmersiveMode - Whether in immersive mode
 * @param {string} panelState - Panel state from parent ('minimized', 'semi', 'expanded')
 * @param {string} activeTab - Currently active tab
 * @param {function} setIsSearchPanelMinimized - State setter for panel minimization
 * 
 * @returns {void} - Side effects only
 */

import { useEffect } from 'react'

export function usePanelStateSync({
  isImmersiveMode,
  panelState,
  activeTab,
  setIsSearchPanelMinimized
}) {
  // Sync isSearchPanelMinimized with panelState prop from ImmersiveHome/EntityDetail
  // panelState can be: 'minimized', 'semi', or 'expanded'
  useEffect(() => {
    if (isImmersiveMode) {
      const shouldBeMinimized = panelState === 'minimized'
      // Only update if there's an actual change to prevent unnecessary re-renders
      setIsSearchPanelMinimized(prev => {
        if (prev !== shouldBeMinimized) {
          return shouldBeMinimized
        }
        return prev
      })
    }
  }, [panelState, isImmersiveMode, setIsSearchPanelMinimized])

  // Handle tab changes in immersive mode
  useEffect(() => {
    if (isImmersiveMode && activeTab && activeTab !== 'search') {
      // Non-search tabs always expand the panel
      setIsSearchPanelMinimized(false)
    }
  }, [activeTab, isImmersiveMode, setIsSearchPanelMinimized])
}
